import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { loanApplicationApi } from '../api/loanApplicationApi';
import { appToast } from '../../../../components/AppToast';
import { appConfirm } from '../../../../components/AppConfirmModal';

export function useLoanApplicationList() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('Pending');
  const [loanTypeFilter, setLoanTypeFilter] = useState('All');
  const [payHeadFilter, setPayHeadFilter] = useState('All');
  const [datePresetFilter, setDatePresetFilter] = useState('Current Month');
  const [fromDate, setFromDate] = useState('2026-08-01');
  const [toDate, setToDate] = useState('2026-08-31');
  const [searchText, setSearchText] = useState('');
  const [searchField, setSearchField] = useState('Reason');
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);


  const statusOptions = [
    { value: 'Pending', label: 'Pending' },
    { value: 'Approved', label: 'Approved' },
    { value: 'Rejected', label: 'Rejected' },
    { value: 'All', label: 'All' },
  ];

  const loanTypeOptions = [
    { value: 'All', label: 'All Loan Types' },
    { value: 'Short Term Loan', label: 'Short Term Loan' },
    { value: 'Long Term Loan', label: 'Long Term Loan' },
    { value: 'Emergency Advance', label: 'Emergency Advance' },
  ];

  const payHeadOptions = [
    { value: 'All', label: 'All Pay Heads' },
    { value: 'Salary Advance', label: 'Salary Advance' },
    { value: 'Personal Loan', label: 'Personal Loan' },
    { value: 'Festival Advance', label: 'Festival Advance' },
    { value: 'Emergency Loan', label: 'Emergency Loan' },
  ];

  const datePresetOptions = [
    { value: 'Current Month', label: 'Current Month' },
    { value: 'Last Month', label: 'Last Month' },
    { value: 'This Quarter', label: 'This Quarter' },
    { value: 'Custom Range', label: 'Custom Range' },
  ];

  const searchFieldOptions = [
    { value: 'Reason', label: 'Reason' },
    { value: 'Remarks', label: 'Remarks' },
    { value: 'Pay Head', label: 'Pay Head' },
  ];

  const tools = [
    { key: 'excel', label: 'Export to Excel', icon: 'table_view' },
    { key: 'pdf', label: 'Export to PDF', icon: 'picture_as_pdf' },
    { key: 'print', label: 'Print', icon: 'print' },
  ];

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await loanApplicationApi.getList();
      setRecords(res.data || []);
    } catch {
      // fallback
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDatePresetChange = useCallback((preset) => {
    setDatePresetFilter(preset);
    if (preset === 'Current Month') {
      setFromDate('2026-08-01');
      setToDate('2026-08-31');
    } else if (preset === 'Last Month') {
      setFromDate('2026-07-01');
      setToDate('2026-07-31');
    }
  }, []);

  // Filtered records
  const filteredRecords = useMemo(() => {
    return records.filter((item) => {
      // Status filter
      if (statusFilter !== 'All' && item.status !== statusFilter) {
        return false;
      }

      // Loan Type filter
      if (loanTypeFilter !== 'All' && item.loanType !== loanTypeFilter) {
        return false;
      }

      // Pay Head filter
      if (payHeadFilter !== 'All' && item.payHead !== payHeadFilter) {
        return false;
      }

      // Text search
      if (searchText.trim()) {
        const query = searchText.trim().toLowerCase();
        if (searchField === 'Reason' && !item.reason?.toLowerCase().includes(query)) return false;
        if (searchField === 'Remarks' && !item.remarks?.toLowerCase().includes(query)) return false;
        if (searchField === 'Pay Head' && !item.payHead?.toLowerCase().includes(query)) return false;
      }

      return true;
    });
  }, [records, statusFilter, loanTypeFilter, payHeadFilter, searchText, searchField]);

  // Pagination
  const totalCount = filteredRecords.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const paginatedRecords = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRecords.slice(start, start + pageSize);
  }, [filteredRecords, page, pageSize]);

  // Actions
  const handleView = useCallback(
    (record) => {
      navigate(`/loan-application/entry?id=${record.id}&mode=view`);
    },
    [navigate]
  );

  const handleEdit = useCallback(
    (record) => {
      navigate(`/loan-application/entry?id=${record.id}&mode=edit`);
    },
    [navigate]
  );

  const handleDelete = useCallback(
    async (id) => {
      const confirmed = await appConfirm({ title: 'Delete Loan Application', message: 'Are you sure you want to delete this loan application?', confirmText: 'Delete', variant: 'danger' });
      if (confirmed) {
        await loanApplicationApi.delete(id);
        appToast.success('Loan application deleted successfully');
        loadData();
      }
    },
    [loadData]
  );

  const handleSearch = useCallback(() => {
    setPage(1);
    appToast.success('Filters applied');
  }, []);

  const resetFilters = useCallback(() => {
    setStatusFilter('All');
    setLoanTypeFilter('All');
    setPayHeadFilter('All');
    setDatePresetFilter('Current Month');
    setFromDate('2026-08-01');
    setToDate('2026-08-31');
    setSearchText('');
    setPage(1);
  }, []);

  const handleToolSelect = useCallback((toolKey) => {
    const key = String(toolKey || '').toLowerCase();
    if (key.includes('print')) {
      window.print();
    } else if (key.includes('excel')) {
      appToast.success('Exporting Loan applications to Excel...');
    } else if (key.includes('pdf')) {
      appToast.success('Exporting Loan applications to PDF...');
    }
  }, []);

  return {
    state: {
      records: paginatedRecords,
      totalCount,
      page,
      pageSize,
      totalPages,
      statusFilter,
      statusOptions,
      loanTypeFilter,
      loanTypeOptions,
      payHeadFilter,
      payHeadOptions,
      datePresetFilter,
      fromDate,
      toDate,
      searchText,
      searchField,
      isLoading,
      datePresetOptions,
      searchFieldOptions,
      tools,
    },
    actions: {
      setStatusFilter,
      setLoanTypeFilter,
      setPayHeadFilter,
      setDatePresetFilter: handleDatePresetChange,
      setFromDate,
      setToDate,
      setSearchText,
      setSearchField,
      setPage,
      setPageSize,
      handleView,
      handleEdit,
      handleDelete,
      handleSearch,
      resetFilters,
      handleToolSelect,
      refresh: loadData,
    },
  };
}
